// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Konto's Speckle panel as data (frontend-ayw.5, ruxd-fk8.4): the status
 * line, where the token comes from, the checks before a token is sent, and
 * the Danish line for each refusal of `PUT`/`DELETE /me/speckle`. Pure, so it
 * is testable without a DOM.
 *
 * The token itself never comes back from ruxd, and nothing here takes it as
 * an argument except the form check, which only reads it.
 */

import { ApiRequestError } from '../api/client';
import type { SpeckleTokenPut, SpeckleTokenStatus } from '../api/types';
import type { Check } from './model';

/** ruxd's default server when `PUT /me/speckle` names none. */
export const DEFAULT_SPECKLE_SERVER = 'https://app.speckle.systems';
/** ruxd refuses a longer token (`'token' is too long`). */
export const MAX_SPECKLE_TOKEN_LENGTH = 512;
/** What the token needs to import from Speckle (Speckle's current scope names, not the old `streams:read`/`profile:read`). */
export const SPECKLE_REQUIRED_SCOPES = ['project.core.read', 'project.version.read'] as const;
/**
 * Only used to show the Speckle account's name in the status line
 * (`status.account`); ruxd accepts a token without it and returns
 * `account: null` instead.
 */
export const SPECKLE_OPTIONAL_SCOPE = 'user.profile.read';

/** Local mode has no allowlist: any https server, or http to this machine. */
export function isLocalSpeckle(status: SpeckleTokenStatus): boolean {
  return status.allowed_servers === null;
}

/** `https://app.speckle.systems` → `app.speckle.systems` (the port stays). */
export function speckleHost(server: string): string {
  return server.replace(/^[a-z]+:\/\//i, '').replace(/\/+$/, '');
}

/**
 * Where a Speckle user makes a personal access token, on `server` — or null
 * when `server` is not an `https://host[:port]` (or `http://` to this
 * machine) URL. Every server string, the allowlist's included, goes through
 * {@link normalizeSpeckleServer} before it becomes an `href`, so a
 * `javascript:` or `data:` entry can never render as a link.
 */
export function speckleTokensLink(server: string): { href: string; host: string } | null {
  if (server.trim() === '') return null;
  const checked = normalizeSpeckleServer(server);
  if (!checked.ok) return null;
  return { href: `${checked.value}/settings/user/developer`, host: speckleHost(checked.value) };
}

const MONTHS = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];

/** `2026-10-30T09:12:00Z` → `30. okt. 2026` (the UTC day: no locale, no clock). */
export function danishDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])}. ${month} ${m[1]}` : iso;
}

export interface SpeckleStatusText {
  /** The panel's one-line state. */
  headline: string;
  /** Second line: when, and which token (`…abcd`); empty when nothing is set. */
  meta: string[];
  /** Where the token comes from and what that means; null when there is nothing to add. */
  sourceHint: string | null;
  /** A problem with the stored token (its server is no longer allowed); null when none. */
  warning: string | null;
}

/** What the panel says about the token's state. */
export function speckleStatusText(status: SpeckleTokenStatus): SpeckleStatusText {
  const host = speckleHost(status.server);
  if (!status.configured) {
    return { headline: 'Ingen Speckle-token', meta: [], sourceHint: null, warning: null };
  }
  const headline = status.account
    ? `Forbundet som ${status.account.name} på ${host}`
    : `Forbundet til ${host}`;
  const meta: string[] = [];
  if (status.updated_at) meta.push(`opdateret ${danishDay(status.updated_at)}`);
  if (status.token_hint) meta.push(`token ${status.token_hint}`);
  let sourceHint: string | null = null;
  if (status.source === 'config')
    sourceHint =
      'Fra serverens konfiguration (--speckle-token eller $SPECKLE_TOKEN), så det kan kun fjernes dér. Et token gemt her bruges i stedet, indtil ruxd genstarter.';
  else if (status.source === 'runtime')
    sourceHint = 'Gemt i hukommelsen — kun indtil ruxd genstarter.';
  const warning = status.server_allowed
    ? null
    : `${host} er ikke længere tilladt på denne server, så tokenet bruges ikke. Gem et token til en tilladt server.`;
  return { headline, meta, sourceHint, warning };
}

/** Whether the panel offers Fjern: a configured token can only be removed from ruxd's configuration. */
export function canRemoveSpeckleToken(status: SpeckleTokenStatus): boolean {
  return status.configured && status.source !== 'config';
}

/**
 * The server the form starts on: the token's own when it is still allowed,
 * else the first allowed one (server mode), else the token's (local mode).
 */
export function initialSpeckleServer(status: SpeckleTokenStatus): string {
  const allowed = status.allowed_servers;
  if (allowed === null) return status.server;
  if (allowed.includes(status.server)) return status.server;
  return allowed[0] ?? '';
}

function isLoopbackHost(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/\.+$/, '');
  return h === 'localhost' || h.endsWith('.localhost') || h === '[::1]' || /^127\.\d+\.\d+\.\d+$/.test(h);
}

/**
 * A local-mode server URL as ruxd takes it: `https://host[:port]` (or
 * `http://` to this machine), no path, query, user or password. Empty means
 * the default server.
 */
export function normalizeSpeckleServer(raw: string): Check<string> {
  const text = raw.trim();
  if (text === '') return { ok: true, value: DEFAULT_SPECKLE_SERVER };
  const bad = { ok: false as const, reason: 'Skriv serverens adresse som https://vært, fx https://app.speckle.systems.' };
  if (!/^https?:\/\//i.test(text)) return bad;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return bad;
  }
  if (url.username || url.password)
    return { ok: false, reason: 'Adressen må ikke indeholde brugernavn eller adgangskode.' };
  if (url.pathname.replace(/\/+/g, '/') !== '/' || url.search || url.hash)
    return { ok: false, reason: 'Skriv kun serverens adresse (https://vært[:port]), uden sti.' };
  if (url.protocol === 'http:' && !isLoopbackHost(url.hostname))
    return { ok: false, reason: 'Speckle-serveren skal være en https://-adresse (http:// kun til denne maskine).' };
  return { ok: true, value: url.origin };
}

export interface SpeckleDraft {
  token: string;
  /** Server mode: one of `allowed_servers`; local mode: what the user typed. */
  server: string;
}

/** The checks before `PUT /me/speckle`; the value is its body. */
export function speckleTokenCheck(draft: SpeckleDraft, status: SpeckleTokenStatus): Check<SpeckleTokenPut> {
  // A pasted token often carries a trailing newline; anything inside is wrong.
  const token = draft.token.trim();
  if (token === '') return { ok: false, reason: 'Indsæt et personligt adgangstoken fra Speckle.' };
  if (token.length > MAX_SPECKLE_TOKEN_LENGTH)
    return { ok: false, reason: 'Tokenet er for langt til at være et Speckle-token.' };
  if (!/^[\x21-\x7e]+$/.test(token))
    return { ok: false, reason: 'Tokenet må kun indeholde synlige tegn uden mellemrum.' };
  const allowed = status.allowed_servers;
  if (allowed !== null) {
    if (allowed.length === 0)
      return { ok: false, reason: 'Serveren tillader ingen Speckle-servere endnu. Kontakt serverens administrator.' };
    if (!allowed.includes(draft.server)) return { ok: false, reason: 'Vælg en af de tilladte Speckle-servere.' };
    return { ok: true, value: { token, server: draft.server } };
  }
  const server = normalizeSpeckleServer(draft.server);
  if (!server.ok) return server;
  return { ok: true, value: { token, server: server.value } };
}

/**
 * The Danish line for each `code` (ruxd-202) a refused `/me/speckle` request
 * can carry that does not depend on `op` — code-first, checked before the
 * status/message fallback below. The two 403 codes (`speckle_account_required`,
 * `auth_session_required`) are handled separately: which line fits depends on
 * `op`, not just the code.
 */
const SPECKLE_ERROR_BY_CODE: Partial<Record<NonNullable<ApiRequestError['code']>, string>> = {
  speckle_token_invalid:
    'Speckle afviste tokenet: det er ugyldigt, udløbet eller tilbagekaldt.',
  speckle_server_not_allowed: 'Den Speckle-server er ikke tilladt her. En administrator tillader servere med --speckle-allowed-server.',
  speckle_server_ip_address: 'Speckle-serveren skal angives med sit navn, ikke en IP-adresse.',
  speckle_server_loopback: 'Speckle-serveren kan ikke være denne maskine.',
  speckle_server_malformed: 'Speckle-serverens adresse er ugyldig. Skriv kun https://vært[:port].',
  speckle_token_malformed: 'Tokenet er ugyldigt: kun synlige tegn uden mellemrum, højst 512.',
  auth_required: 'Du er ikke logget ind længere. Log ind igen, og prøv så igen.',
  speckle_token_from_config: 'Tokenet kommer fra ruxd’s konfiguration (--speckle-token eller $SPECKLE_TOKEN). Fjern det dér, og genstart ruxd.',
  speckle_check_running: 'Dit token bliver allerede tjekket. Vent, til det er færdigt.',
  speckle_check_capacity: 'Der tjekkes mange Speckle-tokens lige nu. Prøv igen om lidt.',
  speckle_server_unreachable: 'Kunne ikke nå Speckle-serveren. Tjek adressen, og prøv igen. Intet blev gemt.',
  speckle_validator_unavailable: 'Denne server kan ikke tjekke Speckle-tokens, så de kan ikke gemmes her.',
  speckle_storage_unavailable:
    'Serveren kan ikke gemme Speckle-tokens endnu: den mangler en krypteringsnøgle. Bed administratoren om at sætte en op (--secret-key-file).',
};

/**
 * `speckle_server_not_https` covers both "https only" and "https or this
 * machine's http" — ruxd tells them apart only in the message, which still
 * matters with a code, so this stays a message check even on the code path.
 */
function httpsOnlyText(message: string): string {
  return /only to this machine/i.test(message)
    ? 'Speckle-serveren skal være en https://-adresse (http:// kun til denne maskine).'
    : 'Speckle-serveren skal være en https://-adresse.';
}

/**
 * The "not your Speckle connection" line: `speckle_account_required` (GET,
 * PUT and DELETE all throw it for a caller with no account at all — a
 * case-scoped token, the superuser token, a share link) reads differently on
 * `load` than on `save`/`remove`, where `auth_session_required` (an API
 * token with an account, just not a session) shares the save/remove wording.
 */
const NO_SESSION_TEXT = 'Speckle-tokenet ændres kun fra en logget-ind session — ikke med et API-token eller et delingslink.';

/** The Danish line under a refused `PUT /me/speckle` (`save`) or `DELETE` (`remove`). */
export function speckleErrorText(cause: unknown, op: 'save' | 'remove' | 'load'): string {
  if (!(cause instanceof ApiRequestError)) return 'Kunne ikke nå serveren.';
  const message = cause.message;
  const { code } = cause;
  if (code === 'speckle_account_required')
    return op === 'load'
      ? 'Speckle-forbindelsen kan kun ses fra din egen konto — ikke fra et delingslink eller et token til én sag.'
      : NO_SESSION_TEXT;
  if (code === 'auth_session_required') return NO_SESSION_TEXT;
  if (code && code !== 'forbidden') {
    if (code === 'speckle_server_not_https') return httpsOnlyText(message);
    const text = SPECKLE_ERROR_BY_CODE[code];
    if (text) return text;
  }
  switch (cause.status) {
    case 400:
      if (/^Speckle refused the token/i.test(message))
        return 'Speckle afviste tokenet: det er ugyldigt, udløbet eller tilbagekaldt.';
      if (/does not allow Speckle server/i.test(message))
        return 'Den Speckle-server er ikke tilladt her. En administrator tillader servere med --speckle-allowed-server.';
      if (/not an IP address/i.test(message)) return 'Speckle-serveren skal angives med sit navn, ikke en IP-adresse.';
      if (/cannot be this machine/i.test(message)) return 'Speckle-serveren kan ikke være denne maskine.';
      if (/must be an https/i.test(message)) return httpsOnlyText(message);
      if (/server URL|allowed Speckle server is empty/i.test(message))
        return 'Speckle-serverens adresse er ugyldig. Skriv kun https://vært[:port].';
      if (/'token'/.test(message)) return 'Tokenet er ugyldigt: kun synlige tegn uden mellemrum, højst 512.';
      return 'Serveren afviste tokenet. Intet blev gemt.';
    case 401:
      return 'Du er ikke logget ind længere. Log ind igen, og prøv så igen.';
    case 403:
      return op === 'load'
        ? 'Speckle-forbindelsen kan kun ses fra din egen konto — ikke fra et delingslink eller et token til én sag.'
        : 'Speckle-tokenet ændres kun fra en logget-ind session — ikke med et API-token eller et delingslink.';
    case 409:
      return 'Tokenet kommer fra ruxd’s konfiguration (--speckle-token eller $SPECKLE_TOKEN). Fjern det dér, og genstart ruxd.';
    case 429:
      return /already running/i.test(message)
        ? 'Dit token bliver allerede tjekket. Vent, til det er færdigt.'
        : 'Der tjekkes mange Speckle-tokens lige nu. Prøv igen om lidt.';
    case 502:
      return 'Kunne ikke nå Speckle-serveren. Tjek adressen, og prøv igen. Intet blev gemt.';
    case 503:
      return /encryption key|cannot store/i.test(message)
        ? 'Serveren kan ikke gemme Speckle-tokens endnu: den mangler en krypteringsnøgle. Bed administratoren om at sætte en op (--secret-key-file).'
        : 'Denne server kan ikke tjekke Speckle-tokens, så de kan ikke gemmes her.';
  }
  if (op === 'load') return 'Speckle-forbindelsen kunne ikke hentes.';
  return op === 'save' ? 'Tokenet kunne ikke gemmes.' : 'Tokenet kunne ikke fjernes.';
}
